/**
 * Generic controller renderer. Given a ControllerSpec, builds the DOM
 * for the slots that are present and wires pointer events to slot-keyed
 * input callbacks. No game knowledge; the host interprets the slots.
 */

import type { ControllerSpec, SlotConfig, ControllerSlot } from '@forgesworn/joystick-mobile';
import { isControllerSpec } from '@forgesworn/joystick-protocol';
import { gameLayout, layoutKey, readPositions } from './player-layout.js';
import { localPointInElement } from './visual-viewport-fit.js';
import { installLayoutEditor } from './layout-editor.js';
import { conformingInput } from './input-grammar.js';

export type SlotInput = (slot: string, value: string) => void;

/** Light tactile click on every button press, when the user hasn't
 *  opted out. Honoured by Android browsers; iOS Safari silently
 *  ignores. Persisted toggle lives at localStorage['pad:haptics']
 *  (anything other than '0' is on). */
const LOCAL_TAP_MS = 12;
function localTap(): void {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('pad:haptics') === '0') return;
  } catch { /* ignore */ }
  const nav = (globalThis as { navigator?: Navigator }).navigator;
  if (!nav || typeof nav.vibrate !== 'function') return;
  try { nav.vibrate(LOCAL_TAP_MS); } catch { /* ignore */ }
}

const FACE_SLOTS: ControllerSlot[] = ['A', 'B', 'X', 'Y'];
const DPAD_SLOTS: ControllerSlot[] = ['dpadU', 'dpadD', 'dpadL', 'dpadR'];
const SHOULDER_SLOTS: ControllerSlot[] = ['L1', 'L2', 'R1', 'R2'];

class ControlScope {
  private controller = new AbortController();
  readonly signal = this.controller.signal;
  private timers = new Set<number>();
  private intervals = new Set<number>();
  private resets = new Set<() => void>();
  addReset(callback: () => void): void { this.resets.add(callback); }
  reset(): void { for (const callback of this.resets) callback(); }

  timeout(callback: () => void, ms: number): void {
    if (this.signal.aborted) return;
    const timer = window.setTimeout(() => {
      this.timers.delete(timer);
      if (!this.signal.aborted) callback();
    }, ms);
    this.timers.add(timer);
  }

  interval(callback: () => void, ms: number): void {
    if (this.signal.aborted) return;
    this.intervals.add(window.setInterval(callback, ms));
  }

  dispose(): void {
    if (this.signal.aborted) return;
    this.controller.abort();
    for (const timer of this.timers) window.clearTimeout(timer);
    for (const timer of this.intervals) window.clearInterval(timer);
    this.timers.clear();
    this.intervals.clear();
  }
}

export function renderControls(
  app: HTMLElement,
  spec: ControllerSpec,
  send: SlotInput,
  status: () => HTMLElement,
): () => void {
  if (!isControllerSpec(spec)) throw Error('Invalid controller definition');
  // Whatever a control computes, the broker only ever sees values in its grammar.
  send = conformingInput(send);
  const scope = new ControlScope();
  // Tear down anything that's there; full re-render on each spec.
  app.replaceChildren(status());
  // Leaving the scanner / idle screens — drop their scroll-column modes.
  app.classList.remove('home-mode', 'idle-mode');
  app.style.backgroundColor = spec.theme?.background ?? '';
  app.classList.remove('profile-mode');
  const motionSlot = spec.slots.joyL?.mode === 'tilt' ? 'joyL' : spec.slots.joyR?.mode === 'tilt' ? 'joyR' : null;
  if (!motionSlot || spec.layout || motionSlot && Object.keys(spec.slots).some(key=>key !== motionSlot && key !== 'A')) return renderProfile(app, spec, send, scope);

  const slots: ControllerSpec['slots'] = spec.theme?.accent
    ? Object.fromEntries(Object.entries(spec.slots).map(([key,cfg])=>[key,{...cfg,colour:cfg!.colour ?? spec.theme!.accent}])) : spec.slots;
  const hasJoyL = !!slots.joyL;
  const hasJoyR = !!slots.joyR;
  const hasDpad = DPAD_SLOTS.some((s) => slots[s]);
  const hasFace = FACE_SLOTS.some((s) => slots[s]);
  const hasShoulders = SHOULDER_SLOTS.some((s) => slots[s]);
  const hasStart = !!slots.start;
  const hasSelect = !!slots.select;

  // Tilt-mode spec gets a dedicated full-screen layout. The whole
  // phone becomes the tilt visualisation + a big BOOST band along
  // the bottom, instead of the cramped split-pane treatment.
  const tiltSlot = (slots.joyL && (slots.joyL.mode as string) === 'tilt') ? 'joyL'
                 : (slots.joyR && (slots.joyR.mode as string) === 'tilt') ? 'joyR'
                 : null;
  if (tiltSlot) {
    app.classList.add('tilt-mode');
    app.classList.remove('pad-mode');
    app.appendChild(makeTiltScreen(tiltSlot, slots[tiltSlot]!, slots.A ?? null, send, scope,spec));
    return () => scope.dispose();
  }
  app.classList.remove('tilt-mode');
  app.classList.add('pad-mode');

  // Top shoulder bar — only mount if any shoulder slot is in the spec.
  const shoulderBar = document.createElement('div');
  shoulderBar.className = 'shoulders';
  if (hasShoulders) {
    const left = document.createElement('div'); left.className = 'side';
    const right = document.createElement('div'); right.className = 'side';
    if (slots.L1) left.appendChild(makeShoulder('L1', slots.L1, send, scope));
    if (slots.L2) left.appendChild(makeShoulder('L2', slots.L2, send, scope));
    if (slots.R1) right.appendChild(makeShoulder('R1', slots.R1, send, scope));
    if (slots.R2) right.appendChild(makeShoulder('R2', slots.R2, send, scope));
    shoulderBar.appendChild(left);
    shoulderBar.appendChild(right);
  }
  app.appendChild(shoulderBar);

  // Main region split into left + right panes.
  const main = document.createElement('div');
  main.className = 'main';
  const leftPane = document.createElement('div'); leftPane.className = 'pane';
  const rightPane = document.createElement('div'); rightPane.className = 'pane';

  if (hasJoyL) {
    // Duck-typed wire mode 'tilt' opts the slot into the accelerometer
    // panel instead of the floating-origin touch joystick.
    if ((slots.joyL!.mode as string) === 'tilt') {
      leftPane.appendChild(makeTilt('joyL', send, scope));
    } else {
      leftPane.appendChild(makeJoystick('joyL', slots.joyL!, send, scope));
    }
  } else if (hasDpad) leftPane.appendChild(makeDpad(slots, send, scope));

  if (hasFace) rightPane.appendChild(makeFace(slots, send, scope));
  if (hasJoyR) {
    if (hasFace) rightPane.classList.add('mixed-controls');
    if ((slots.joyR!.mode as string) === 'tilt') {
      rightPane.appendChild(makeTilt('joyR', send, scope));
    } else {
      rightPane.appendChild(makeJoystick('joyR', slots.joyR!, send, scope));
    }
  }

  main.appendChild(leftPane);
  main.appendChild(rightPane);
  app.appendChild(main);

  // Bottom bar with start / select if either is in the spec.
  const bottom = document.createElement('div');
  bottom.className = 'bottombar';
  if (hasSelect) bottom.appendChild(makeSmall('select', slots.select!, send, scope));
  if (hasStart) bottom.appendChild(makeSmall('start', slots.start!, send, scope));
  app.appendChild(bottom);
  return () => scope.dispose();
}

function makeShoulder(slot: string, cfg: SlotConfig, send: SlotInput, scope: ControlScope): HTMLElement {
  const el = document.createElement('div');
  el.className = 'shoulder-btn';
  if(cfg.colour) el.style.borderColor=cfg.colour;
  el.textContent = cfg.label ?? slot;
  addGraphic(el, cfg);
  el.setAttribute('aria-label',cfg.label ?? slot);
  wirePress(el, slot, send, scope);
  return el;
}

function makeSmall(slot: string, cfg: SlotConfig, send: SlotInput, scope: ControlScope): HTMLElement {
  const el = document.createElement('div');
  el.className = 'small-btn';
  if(cfg.colour) el.style.borderColor=cfg.colour;
  el.textContent = cfg.label ?? slot.toUpperCase();
  addGraphic(el, cfg);
  el.setAttribute('aria-label',cfg.label ?? slot);
  wirePress(el, slot, send, scope);
  return el;
}

function makeFace(slots: ControllerSpec['slots'], send: SlotInput, scope: ControlScope): HTMLElement {
  const face = document.createElement('div');
  face.className = 'face';
  for (const slot of FACE_SLOTS) {
    const cfg = slots[slot];
    if (!cfg) continue;
    const btn = document.createElement('div');
    btn.className = `face-btn ${slot.toLowerCase()}`;
    if (cfg.colour) btn.style.setProperty('--colour', cfg.colour);
    const icon = document.createElement('span');
    icon.textContent = cfg.icon ?? slot;
    btn.appendChild(icon);
    addGraphic(icon, cfg);
    if (cfg.label) {
      const lbl = document.createElement('span');
      lbl.className = 'label';
      lbl.textContent = cfg.label;
      btn.appendChild(lbl);
    }
    wirePress(btn, slot, send, scope);
    face.appendChild(btn);
  }
  return face;
}

function makeDpad(slots: ControllerSpec['slots'], send: SlotInput, scope: ControlScope): HTMLElement {
  const dpad = document.createElement('div');
  dpad.className = 'dpad';
  const dirs: Array<{ slot: ControllerSlot; cls: string; glyph: string }> = [
    { slot: 'dpadU', cls: 'u', glyph: '▲' },
    { slot: 'dpadD', cls: 'd', glyph: '▼' },
    { slot: 'dpadL', cls: 'l', glyph: '◀' },
    { slot: 'dpadR', cls: 'r', glyph: '▶' },
  ];
  for (const { slot, cls, glyph } of dirs) {
    const cfg = slots[slot];
    if (!cfg) continue;
    const b = document.createElement('div');
    b.className = `dpad-btn ${cls}`;
    b.textContent = cfg.icon ?? glyph;
    if(cfg.colour)b.style.borderColor=cfg.colour;
    b.setAttribute('aria-label',cfg.label ?? slot);
    addGraphic(b, cfg);
    wirePress(b, slot, send, scope);
    dpad.appendChild(b);
  }
  return dpad;
}

/** Wire pointerdown / up / cancel / leave to '1' / '0' on a slot.
 *  No pointer capture and no re-entry guard on purpose: capturing a
 *  small button is revoked unpredictably on iOS (a lostpointercapture
 *  with no matching pointerup) which wedged the slot. Plain down/up,
 *  with pointerleave covering a finger that slides off, is robust. */
function wirePress(el: HTMLElement, slot: string, send: SlotInput, scope: ControlScope): void {
  el.dataset.controlSlot=slot;
  let pressed = false;
  const press = (e: Event): void => {
    if (pressed) return;
    pressed = true;
    el.classList.add('pressed');
    send(slot, '1');
    localTap();
    e.preventDefault();
  };
  const release = (): void => {
    if (!pressed) return;
    pressed = false;
    el.classList.remove('pressed');
    send(slot, '0');
  };
  if(el.tagName !== 'BUTTON') {el.setAttribute('role','button');el.tabIndex=0;}
  if(!el.hasAttribute('aria-label')) el.setAttribute('aria-label',el.textContent ?? slot);
  el.addEventListener('pointerdown', press);
  el.addEventListener('keydown', e => { if (['Enter',' '].includes(e.key) && !e.repeat) press(e); },{signal:scope.signal});
  el.addEventListener('keyup', e => { if (['Enter',' '].includes(e.key)) { e.preventDefault(); release(); } },{signal:scope.signal});
  el.addEventListener('pointerup', release);
  el.addEventListener('pointercancel', release);
  el.addEventListener('pointerleave', release);
  // Cancel the touch default so mashing a button is never read as an
  // iOS double-tap (which zooms the page). Pointer events still fire,
  // so the press itself is unaffected.
  el.addEventListener('touchend', (e) => e.preventDefault(), { passive: false });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) release();
  }, { signal: scope.signal });
  el.addEventListener('blur',release,{signal:scope.signal});
  scope.signal.addEventListener('abort', release, { once: true });
  scope.addReset(release);
}

/** Floating-origin joystick. Origin captured on pointerdown; heading
 *  emitted on move. Thrust toggled at a deadzone threshold. Quick tap
 *  with little movement triggers tapAction if the slot config supplies
 *  one. */
function makeJoystick(slot: string, cfg: SlotConfig, send: SlotInput, scope: ControlScope, withPower = false): HTMLElement {
  const root = document.createElement('div');
  root.className = 'joystick';
  root.dataset.controlSlot=slot;
  const base = document.createElement('div'); base.className = 'base';
  const knob = document.createElement('div'); knob.className = 'knob';
  root.appendChild(base);
  root.appendChild(knob);
  root.setAttribute('aria-label',cfg.label || (slot === 'joyL' ? 'Left stick' : 'Right stick'));
  if(cfg.colour) {base.style.borderColor=cfg.colour;knob.style.backgroundColor=cfg.colour;}
  addGraphic(knob, cfg);

  const TAP_MAX_DURATION_MS = 220;
  const TAP_MAX_DISTANCE = 10;     // px before it stops counting as a tap
  const THRUST_THRESHOLD = 18;     // px from origin before thrust fires
  const KNOB_MAX_TRAVEL = 50;      // visual knob distance from origin

  let activePointer: number | null = null;
  let originX = 0, originY = 0;
  let lastAngle = NaN;
  let thrusting = false;
  let pressStart = 0;
  let maxDist = 0;
  let lastSample = -Infinity;
  let heldDx = 0, heldDy = 0;

  const onDown = (e: PointerEvent): void => {
    // Always adopt the latest pointer. No re-entry guard: if a prior
    // gesture's release was missed, bailing here would wedge the stick.
    activePointer = e.pointerId;
    try { root.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const p = localPointInElement(root, e);
    originX = p.x;
    originY = p.y;
    base.style.left = `${originX}px`;
    base.style.top = `${originY}px`;
    knob.style.left = `${originX}px`;
    knob.style.top = `${originY}px`;
    root.classList.add('active');
    pressStart = performance.now();
    maxDist = 0;
    lastAngle = NaN;
    lastSample = -Infinity;
    heldDx = heldDy = 0;
    thrusting = false;
    e.preventDefault();
  };

  const onMove = (e: PointerEvent): void => {
    if (activePointer !== e.pointerId) return;
    const p = localPointInElement(root, e);
    const x = p.x;
    const y = p.y;
    const dx = x - originX;
    const dy = y - originY;
    heldDx = dx; heldDy = dy;
    const dist = Math.hypot(dx, dy);
    if (dist > maxDist) maxDist = dist;

    const sampled = performance.now() - lastSample >= 50;
    if (sampled) lastSample = performance.now();
    if (cfg.mode === 'xy' && sampled) {
      const divisor = Math.max(KNOB_MAX_TRAVEL, dist);
      send(`${slot}-x`, String(Math.round(dx / divisor * 1000)));
      send(`${slot}-y`, String(Math.round(dy / divisor * 1000)));
    }
    // Heading: angle in radians, 0 = right, increasing clockwise (DOM Y is down).
    const angle = Math.atan2(dy, dx);
    if (cfg.mode !== 'xy' && sampled && (!Number.isFinite(lastAngle) || Math.abs(angle - lastAngle) > 0.01)) {
      lastAngle = angle;
      send(slot, String(Math.round(angle * 1000)));
    }

    // Thrust flips at the deadzone boundary, never spammed.
    const want = dist >= THRUST_THRESHOLD;
    if (want !== thrusting) {
      thrusting = want;
      send(`${slot}-thrust`, want ? '1' : '0');
    }
    if (withPower && sampled) send(`${slot}-power`, String(want ? Math.round(Math.min(1, dist / KNOB_MAX_TRAVEL) * 1000) : 0));

    // Visual knob position, clamped to KNOB_MAX_TRAVEL.
    const clamped = Math.min(dist, KNOB_MAX_TRAVEL);
    const kx = originX + (clamped / Math.max(dist, 0.0001)) * dx;
    const ky = originY + (clamped / Math.max(dist, 0.0001)) * dy;
    knob.style.left = `${kx}px`;
    knob.style.top = `${ky}px`;
  };

  const onUp = (e: PointerEvent): void => {
    if (activePointer !== e.pointerId) return;
    activePointer = null;
    try { root.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    root.classList.remove('active');
    const elapsed = performance.now() - pressStart;
    const wasTap = e.type === 'pointerup' && elapsed < TAP_MAX_DURATION_MS && maxDist < TAP_MAX_DISTANCE;
    if (thrusting) {
      thrusting = false;
      send(`${slot}-thrust`, '0');
    }
    send(`${slot}-end`, '1');
    if (cfg.mode === 'xy') { send(`${slot}-x`, '0'); send(`${slot}-y`, '0'); }
    if (withPower) send(`${slot}-power`, '0');
    if (wasTap && cfg.tapAction) {
      send(`${slot}-tap`, '1');
      localTap();
    }
  };

  scope.interval(()=> {
    if(activePointer === null || document.hidden || performance.now()-lastSample < 50)return;
    lastSample = performance.now();
    const distance = Math.hypot(heldDx,heldDy), divisor = Math.max(KNOB_MAX_TRAVEL,distance);
    if(cfg.mode === 'xy') {send(`${slot}-x`,String(Math.round(heldDx/divisor*1000)));send(`${slot}-y`,String(Math.round(heldDy/divisor*1000)));}
    else send(slot,String(Math.round(Math.atan2(heldDy,heldDx)*1000)));
    send(`${slot}-thrust`,thrusting ? '1' : '0');
    send(`${slot}-power`,thrusting ? String(Math.round(Math.min(1,distance/KNOB_MAX_TRAVEL)*1000)) : '0');
  },50);
  root.addEventListener('pointerdown', onDown);
  root.addEventListener('pointermove', onMove);
  root.addEventListener('pointerup', onUp);
  root.addEventListener('pointercancel', onUp);
  // Cancel the touch default so a quick double-tap on the stick is not
  // read as an iOS double-tap-zoom. Pointer events still fire.
  root.addEventListener('touchend', (e) => e.preventDefault(), { passive: false });
  // Backgrounded mid-drag: synthesise a release so the stick doesn't
  // stay wedged thrusting while the phone is away. The joystick keeps
  // pointer capture (the finger leaves the small pad box by design);
  // recovery from a lost capture is onDown adopting the next pointer.
  const reset = (): void => {
    if (activePointer !== null) {
      activePointer = null;
      root.classList.remove('active');
      if (thrusting) { thrusting = false; send(`${slot}-thrust`, '0'); }
      send(`${slot}-end`, '1');
      if (cfg.mode === 'xy') { send(`${slot}-x`, '0'); send(`${slot}-y`, '0'); }
      if (withPower) send(`${slot}-power`, '0');
    }
  };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) reset();
  }, { signal: scope.signal });
  scope.signal.addEventListener('abort', reset, { once: true });
  scope.addReset(reset);

  return root;
}

function addGraphic(element: HTMLElement, cfg: SlotConfig): void {
  if (!cfg.graphic) return;
  const fallback = element.textContent ?? cfg.icon ?? '';
  element.replaceChildren();
  const image = document.createElement('img'); image.alt = ''; image.draggable = false;
  image.addEventListener('error',()=> {element.textContent=fallback;},{once:true});
  image.src = `data:image/png;base64,${cfg.graphic.png}`;
  image.style.cssText = 'width:100%;height:100%;max-width:48px;max-height:48px;object-fit:contain;pointer-events:none';
  element.appendChild(image);
}

function renderProfile(app: HTMLElement, spec: ControllerSpec, send: SlotInput, scope: ControlScope): () => void {
  app.classList.remove('pad-mode','tilt-mode'); app.classList.add('profile-mode');
  const surface = document.createElement('div'); surface.className = 'profile-surface'; app.appendChild(surface);
  let editor: ReturnType<typeof installLayoutEditor> | undefined;
  const rawSend = send; send = (key,value) => { if(!editor?.editing() || value === '0' || key.endsWith('-end')) rawSend(key,value); };
  const controls = new Map<string, HTMLElement>();
  for (const [key, cfg] of Object.entries(spec.slots)) {
    const node = key.startsWith('joy') ? makeJoystick(key,cfg!,send,scope,true) : document.createElement('button');
    if (!key.startsWith('joy')) {
      const button = node as HTMLButtonElement; button.type = 'button'; button.className = 'profile-button';
      const glyph = document.createElement('span'); glyph.textContent = cfg!.icon ?? key; addGraphic(glyph,cfg!); button.appendChild(glyph);
      if (cfg!.label) { const label = document.createElement('small'); label.textContent = cfg!.label; button.appendChild(label); }
      wirePress(button,key,send,scope);

    }
    node.setAttribute('aria-label',cfg!.label || (key === 'joyL' ? 'Left stick' : key === 'joyR' ? 'Right stick' : key));
    node.style.setProperty('--colour',cfg!.colour ?? spec.theme?.accent ?? '#b48cff');
    controls.set(key,node); surface.appendChild(node);
  }
  addProfileMotion(app,spec,controls,send,scope,()=>!editor?.editing());
  let lastSize = '';
  const layout = (force = false): void => {
    const w = surface.clientWidth, h = surface.clientHeight; if (!w || !h) return;
    const size = `${w}:${h}`; if (size === lastSize && !force) return;
    if (lastSize && size !== lastSize) scope.reset(); lastSize = size;
    const map = editor!.resolve(gameLayout(spec,w,h),w,h);
    for (const [key,node] of controls) {
      const stick = key.startsWith('joy'), p = map[key]; if (!p) continue;
      if (stick) {
        node.style.setProperty('--stick-x',`${p.x-p.zone.x}px`);
        node.style.setProperty('--stick-y',`${p.y-p.zone.y}px`);
        node.style.setProperty('--stick-size',`${p.size}px`);
        const r = p.zone; node.style.cssText += `;position:absolute;left:${r.x}px;top:${r.y}px;width:${r.width}px;height:${r.height}px;z-index:1`;
        const base = node.querySelector<HTMLElement>('.base')!, knob = node.querySelector<HTMLElement>('.knob')!;
        base.style.width = base.style.height = `${p.size}px`; knob.style.width = knob.style.height = `${p.size*.3}px`;
        for (const part of [base,knob]) { part.style.left = `${p.x-r.x}px`; part.style.top = `${p.y-r.y}px`; }
      } else node.style.cssText += `;position:absolute;left:${p.zone.x}px;top:${p.zone.y}px;width:${p.zone.width}px;height:${p.zone.height}px;border-radius:${p.shape === 'circle' ? '50%' : '18px'};z-index:2`;
    }
  };
  editor = installLayoutEditor(app,surface,spec,scope,()=>layout(true));
  const observer = new ResizeObserver(()=>layout()); observer.observe(surface);
  layout();
  return () => { observer.disconnect(); scope.dispose(); app.classList.remove('profile-mode'); };
}

/** Motion is an explicit player choice, independent of the game's artwork/layout. */
function addProfileMotion(app: HTMLElement,spec: ControllerSpec,controls: Map<string,HTMLElement>,send: SlotInput,scope: ControlScope,enabled = ()=>true): void {
  const slot = (['joyL','joyR'].find(key=>spec.slots[key as ControllerSlot]?.mode === 'tilt') ?? ['joyL','joyR'].find(key=>spec.slots[key as ControllerSlot])) as ControllerSlot | undefined;
  if(!slot) return;
  const toolbar = document.createElement('div'); toolbar.className = 'profile-motion';
  const toggle = document.createElement('button'); toggle.type = 'button'; toggle.textContent = 'Tilt to steer';
  const recentre = document.createElement('button'); recentre.type = 'button'; recentre.textContent = 'Recentre'; recentre.hidden = true;
  const hint = document.createElement('span'); hint.setAttribute('role','status'); hint.textContent = 'Touch controls';
  toolbar.append(toggle,recentre,hint); app.insertBefore(toolbar,app.querySelector('.profile-surface'));
  const cfg = spec.slots[slot]!, node = controls.get(slot)!;
  let active = false, generation = 0, baseline: {x:number;y:number;rotation:number}|null = null, lastRead = 0, lastSend = -Infinity;
  const neutral = () => { send(`${slot}-thrust`,'0'); send(`${slot}-power`,'0'); send(`${slot}-end`,'1'); if(cfg.mode === 'xy') { send(`${slot}-x`,'0'); send(`${slot}-y`,'0'); } };
  const reset = () => { if(active) neutral(); baseline = null; lastSend = -Infinity; node.querySelector<HTMLElement>('.knob')?.style.removeProperty('transform'); };
  const stop = (message = 'Touch controls') => { ++generation; reset(); active = false; node.style.pointerEvents = ''; toggle.textContent = 'Tilt to steer'; toggle.setAttribute('aria-pressed','false'); recentre.hidden = true; hint.textContent = message; };
  toggle.setAttribute('aria-pressed','false');
  toggle.addEventListener('click',async()=> {
    if(!enabled())return;
    if(active) { stop(); return; }
    const epoch = ++generation;
    try {
      const event = DeviceOrientationEvent as typeof DeviceOrientationEvent & {requestPermission?:()=>Promise<string>};
      if(event.requestPermission && await event.requestPermission() !== 'granted') { hint.textContent = 'Motion access declined · use touch'; return; }
      if(epoch !== generation || scope.signal.aborted || document.hidden) return;
      scope.reset(); active = true; baseline = null; lastRead = performance.now(); node.style.pointerEvents = 'none'; toggle.textContent = 'Use touch'; toggle.setAttribute('aria-pressed','true'); recentre.hidden = false; hint.textContent = 'Hold comfortably · tilt to steer';
    } catch { hint.textContent = 'Motion unavailable · use touch'; }
  },{signal:scope.signal});
  recentre.addEventListener('click',reset,{signal:scope.signal});
  const rotation = () => ((screen.orientation?.angle ?? (window as Window & {orientation?:number}).orientation ?? 0)+360)%360;
  window.addEventListener('deviceorientation',ev=> {
    if(!active || !enabled() || document.hidden || ev.gamma === null || ev.beta === null || !Number.isFinite(ev.gamma) || !Number.isFinite(ev.beta)) return;
    const now = performance.now(), r = rotation(), gx = ev.gamma, gy = -ev.beta;
    const x = r === 90 ? gy : r === 180 ? -gx : r === 270 ? -gy : gx;
    const y = r === 90 ? -gx : r === 180 ? -gy : r === 270 ? gx : gy;
    lastRead = now;
    if(!baseline || baseline.rotation !== r) { reset(); baseline = {x,y,rotation:r}; return; }
    if(now-lastSend < 50) return; lastSend = now;
    let dx = (x-baseline.x)/35, dy = (y-baseline.y)/35; const magnitude = Math.hypot(dx,dy), divisor = Math.max(1,magnitude);
    dx /= divisor; dy /= divisor; const moving = magnitude >= 4/35;
    if(cfg.mode === 'xy') { send(`${slot}-x`,String(moving ? Math.round(dx*1000) : 0)); send(`${slot}-y`,String(moving ? Math.round(dy*1000) : 0)); }
    else if(moving) send(slot,String(Math.round(Math.atan2(dy,dx)*1000)));
    const knob = node.querySelector<HTMLElement>('.knob'); if(knob) knob.style.transform = `translate(calc(-50% + ${dx*50}px),calc(-50% + ${dy*50}px))`;
    send(`${slot}-thrust`,moving ? '1' : '0'); send(`${slot}-power`,moving ? String(Math.round(Math.min(1,magnitude)*1000)) : '0');
  },{signal:scope.signal});
  window.addEventListener('orientationchange',reset,{signal:scope.signal});
  document.addEventListener('visibilitychange',()=> { if(document.hidden) stop(); },{signal:scope.signal});
  scope.interval(()=> { if(active && performance.now()-lastRead > 1500) stop('Sensor unavailable · use touch'); },100);
  scope.addReset(reset); scope.signal.addEventListener('abort',()=>stop(),{once:true});
}

/** Full-screen tilt layout used in place of the normal split-pane grid
 *  when the spec opts into tilt mode. Renders:
 *
 *    - A large bubble-level arena filling most of the screen, with a
 *      neutral cross + direction arrow + power ring.
 *    - A big BOOST lozenge along the bottom that's easy to thumb-press
 *      while still tilting.
 *    - A recalibrate hint / button so the user can re-zero if their
 *      hold drifts.
 *
 *  Device orientation is read in device-frame (gamma/beta), then
 *  rotated into screen-frame via screen.orientation.angle so that
 *  "tilt right" always sends the marble right regardless of whether
 *  the phone is in portrait or landscape. Calibration captures the
 *  user's natural hold ~1.2s after activation so the player doesn't
 *  have to hold the phone "flat". */
function makeTiltScreen(slot: string, _cfg: SlotConfig, aCfg: SlotConfig | null, send: SlotInput, scope: ControlScope,spec:ControllerSpec): HTMLElement {
  let editor: ReturnType<typeof installLayoutEditor> | undefined;
  const rawSend=send;send=(key,value)=>{if(!editor?.editing()||value==='0'||key.endsWith('-end'))rawSend(key,value);};
  const root = document.createElement('div');
  root.className = 'tilt-screen';

  const arena = document.createElement('div');
  arena.className = 'tilt-arena';
  const cross = document.createElement('div'); cross.className = 'tilt-cross';
  const dz = document.createElement('div'); dz.className = 'tilt-deadzone';
  const ring = document.createElement('div'); ring.className = 'tilt-ring';
  const bubble = document.createElement('div'); bubble.className = 'tilt-bubble';
  addGraphic(bubble,_cfg);

  // Live sensor activity dot — flashes green for ~100ms each time an
  // orientation event arrives. If the dot's dark, the sensor isn't
  // delivering data and the user can stop wondering why tilt isn't
  // working.
  const liveDot = document.createElement('div'); liveDot.className = 'tilt-live-dot';
  const liveLabel = document.createElement('div'); liveLabel.className = 'tilt-live-label';
  liveLabel.textContent = 'SENSOR';

  // Small numeric readout — visible by default so the user can see
  // sensor values + screen orientation + event counter. Doubles as
  // 'is this thing on?' confirmation.
  const debug = document.createElement('div'); debug.className = 'tilt-debug';
  const dbgB = document.createElement('span');
  const dbgG = document.createElement('span');
  const dbgR = document.createElement('span');
  const dbgE = document.createElement('span');
  dbgB.textContent = 'β —';
  dbgG.textContent = 'γ —';
  dbgR.textContent = 'rot —°';
  dbgE.textContent = 'evt 0';
  debug.appendChild(dbgB);
  debug.appendChild(dbgG);
  debug.appendChild(dbgR);
  debug.appendChild(dbgE);

  // Explicit RE-CENTRE button — large, obvious, sits where the thumb
  // expects it. Replaces the old tap-anywhere-on-arena gesture which
  // was a footgun (any stray tap shifted the neutral point).
  const recentreBtn = document.createElement('button');
  recentreBtn.type = 'button';
  recentreBtn.className = 'tilt-recentre-btn';
  recentreBtn.textContent = 'RE-CENTRE';
  recentreBtn.style.display = 'none';

  // Enable scrim — full-arena modal shown until motion access is
  // granted. Tap-anywhere-on-this triggers the iOS permission prompt.
  // Stays mounted under everything else so the moment permission
  // resolves we can drop it without rebuilding the DOM.
  const enableScrim = document.createElement('div');
  enableScrim.className = 'tilt-enable-scrim';
  const enableTitle = document.createElement('div');
  enableTitle.className = 'tilt-enable-title';
  enableTitle.textContent = 'TAP TO ENABLE TILT';
  const enableSub = document.createElement('div');
  enableSub.className = 'tilt-enable-sub';
  enableSub.textContent = "iOS will ask for motion access — tap Allow";
  enableScrim.appendChild(enableTitle);
  enableScrim.appendChild(enableSub);

  arena.appendChild(cross);
  arena.appendChild(dz);
  arena.appendChild(ring);
  arena.appendChild(bubble);
  arena.appendChild(liveDot);
  arena.appendChild(liveLabel);
  arena.appendChild(debug);
  arena.appendChild(recentreBtn);
  arena.appendChild(enableScrim);
  root.appendChild(arena);

  // Boost band — full-width lozenge along the bottom of the screen.
  const boost = document.createElement('div');
  boost.className = 'tilt-boost';
  const boostInner = document.createElement('div');
  boostInner.className = 'tilt-boost-inner';
  const boostGlyph = document.createElement('span');
  boostGlyph.textContent = aCfg?.icon ?? '⏩';
  if(aCfg)addGraphic(boostGlyph,aCfg);
  const boostLabel = document.createElement('span');
  boostLabel.className = 'tilt-boost-label';
  boostLabel.textContent = aCfg?.label ?? 'BOOST';
  boostInner.appendChild(boostGlyph);
  boostInner.appendChild(boostLabel);
  boost.appendChild(boostInner);
  if (aCfg) wirePress(boost, 'A', send, scope);
  root.appendChild(boost);
  const touchButton = document.createElement('button');
  touchButton.type = 'button';
  touchButton.className = 'tilt-touch-btn';
  touchButton.textContent = 'USE TOUCH CONTROLS';
  root.appendChild(touchButton);

  // ── Orientation state ──────────────────────────────────────────
  let active = false;
  let motionEnabled = false;
  let touchMode = false;
  let lastSendAt = 0;
  let lastAngle = NaN;
  let thrusting = false;
  let lastThrustRefreshAt = 0;
  let lastEventAt = 0;
  let eventCount = 0;
  let sensorStale = false;
  const DEADZONE_ON  = 0.07;
  const DEADZONE_OFF = 0.04;
  const MAX_TILT_DEG = 16;
  let baselineX = 0, baselineY = 0;
  let baselineLocked = false;

  const stopMotion = (): void => {
    thrusting = false;
    lastAngle = NaN;
    lastSendAt = 0;
    lastThrustRefreshAt = 0;
    send(`${slot}-thrust`, '0');
    send(`${slot}-power`, '0');
    bubble.style.left = '50%';
    bubble.style.top = '50%';
    bubble.style.boxShadow = '';
    ring.style.opacity = '0.3';
    ring.style.transform = 'translate(-50%, -50%) scale(0.5)';
  };

  /** Effective screen rotation. Tries the orientation API first, then
   *  window.orientation (legacy iOS), and finally falls back to a
   *  width/height heuristic so we cope on PWA standalones that under-
   *  report the angle. */
  const screenAngle = (): number => {
    const s = (globalThis as { screen?: { orientation?: { angle?: number } } }).screen;
    const a = s?.orientation?.angle;
    if (typeof a === 'number') return a;
    const wo = (globalThis as { orientation?: number }).orientation;
    if (typeof wo === 'number') return wo < 0 ? wo + 360 : wo;
    // Heuristic — if the window is wider than it is tall, treat as a
    // 90° rotation. We can't tell 90 vs 270 from dimensions alone, so
    // default to 90. The user can flip if it's wrong via the
    // recentre + swap-axis path; in practice 90 is the common case.
    if (window.innerWidth > window.innerHeight) return 90;
    return 0;
  };

  /** Map device-frame (gamma, beta) to screen-frame (sx = right, sy = down)
   *  using marble-on-tray physics: a positive tilt of any edge means
   *  that edge dips toward the floor, so a marble on the screen rolls
   *  TOWARD that edge.
   *
   *  Portrait (angle 0): top-edge-down (beta+) → marble rolls UP on
   *  screen (toward the dipped top edge) → sy is negative.
   *  Right-edge-down (gamma+) → marble rolls RIGHT → sx positive.
   *
   *  All four orientations derived the same way from the device-edge
   *  the user sees on each side. */
  const toScreenFrame = (gamma: number, beta: number): { sx: number; sy: number } => {
    const a = screenAngle();
    if (a === 90)  return { sx: -beta,  sy:  gamma };
    if (a === 180) return { sx: -gamma, sy:  beta  };
    if (a === 270) return { sx:  beta,  sy: -gamma };
    return                   { sx:  gamma, sy: -beta };  // 0 / portrait
  };

  const recentre = (): void => {
    stopMotion();
    baselineLocked = false;
    const nav = (globalThis as { navigator?: Navigator }).navigator;
    try { nav?.vibrate?.(20); } catch { /* ignore */ }
  };

  const onOrientation = (ev: DeviceOrientationEvent): void => {
    if (!active || editor?.editing() || touchMode || document.hidden || scope.signal.aborted) return;
    const rawGamma = ev.gamma;
    const rawBeta = ev.beta;
    // Missing or invalid readings cannot keep a dead sensor stream alive
    // or be interpreted as the phone suddenly tilting towards zero.
    if (rawGamma === null || rawBeta === null || !Number.isFinite(rawGamma) || !Number.isFinite(rawBeta)) return;
    lastEventAt = performance.now();
    eventCount += 1;
    sensorStale = false;
    recentreBtn.textContent = 'RE-CENTRE';
    touchButton.textContent = 'USE TOUCH CONTROLS';

    // Live dot — flash for ~120ms each event so the user knows we're
    // receiving data even when the bubble is centred.
    liveDot.classList.add('on');
    scope.timeout(() => liveDot.classList.remove('on'), 120);

    // Debug numbers
    dbgB.textContent = `β ${Math.round(rawBeta)}°`;
    dbgG.textContent = `γ ${Math.round(rawGamma)}°`;
    dbgR.textContent = `rot ${screenAngle()}°`;
    if (eventCount % 5 === 0) dbgE.textContent = `evt ${eventCount}`;

    const { sx, sy } = toScreenFrame(rawGamma, rawBeta);

    if (!baselineLocked) {
      // Lock the baseline on the first event with any non-zero reading.
      // iOS Safari very occasionally emits a few zero-event warm-up
      // ticks before the sensor delivers real data; skip those.
      if (rawGamma !== 0 || rawBeta !== 0) {
        baselineX = sx;
        baselineY = sy;
        baselineLocked = true;
        recentreBtn.style.display = 'block';
      }
      return;
    }

    const x = clamp((sx - baselineX) / MAX_TILT_DEG, -1, 1);
    const y = clamp((sy - baselineY) / MAX_TILT_DEG, -1, 1);
    const mag = Math.min(1, Math.hypot(x, y));
    const angle = Math.atan2(y, x);

    const bx = 50 + x * 38;
    const by = 50 + y * 38;
    bubble.style.left = `${bx}%`;
    bubble.style.top  = `${by}%`;
    // Beef up the bubble's glow at higher tilt so direction reads
    // immediately without a separate arrow indicator.
    bubble.style.boxShadow = `0 0 ${10 + mag * 30}px rgba(180,140,255,${0.5 + mag * 0.4}), inset 0 -3px 8px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.4)`;
    ring.style.opacity = String(0.3 + 0.7 * mag);
    ring.style.transform = `translate(-50%, -50%) scale(${0.5 + mag * 0.5})`;

    const now = performance.now();
    if (now - lastSendAt < 33) return;
    lastSendAt = now;

    const thrustThreshold = thrusting ? DEADZONE_OFF : DEADZONE_ON;
    if (mag > thrustThreshold) {
      // Re-send thrust=1 on the leading edge AND as a periodic refresh
      // every ~500ms. Defensive against the host transiently clearing
      // input state (e.g. between races during the countdown freeze).
      if (!thrusting || now - lastThrustRefreshAt > 500) {
        thrusting = true;
        lastThrustRefreshAt = now;
        send(`${slot}-thrust`, '1');
      }
      if (!Number.isFinite(lastAngle) || Math.abs(angle - lastAngle) > 0.01) {
        lastAngle = angle;
        send(slot, String(Math.round(angle * 1000)));
      }
      send(`${slot}-power`, String(Math.round(mag * 1000)));
    } else if (thrusting) {
      thrusting = false;
      send(`${slot}-thrust`, '0');
      send(`${slot}-power`, '0');
    }
  };

  /** Attach the orientation listener. iOS prefers default bubble phase
   *  (no third arg) and silently misbehaves with capture=true in
   *  standalone PWAs. */
  const attach = (): void => {
    if (active || !motionEnabled || touchMode || document.hidden || scope.signal.aborted) return;
    active = true;
    arena.classList.add('active');
    window.addEventListener('deviceorientation', onOrientation, { signal: scope.signal });
    // Best-effort: some Android builds + a few Safari versions deliver
    // motion only on the absolute channel.
    window.addEventListener('deviceorientationabsolute', onOrientation as EventListener, { signal: scope.signal });
  };

  const detach = (): void => {
    if (!active) return;
    window.removeEventListener('deviceorientation', onOrientation);
    window.removeEventListener('deviceorientationabsolute', onOrientation as EventListener);
    active = false;
  };

  const useTouch = (): void => {
    if (touchMode || scope.signal.aborted) return;
    touchMode = true;
    detach();
    stopMotion();
    arena.replaceChildren(makeJoystick(slot, { ..._cfg, mode: 'heading', tapAction: undefined }, send, scope, true));
    root.classList.add('touch-mode');
    touchButton.textContent = 'DRAG TO STEER · RELEASE TO STOP';
    touchButton.disabled = true;
    try { sessionStorage.setItem('joystick:tilt-controls', 'touch'); } catch { /* Optional preference. */ }
  };
  touchButton.addEventListener('click', useTouch, { signal: scope.signal });

  // ── Permission gate (iOS) ──────────────────────────────────────
  const REQUEST_PERMISSION = (globalThis.DeviceOrientationEvent as unknown as {
    requestPermission?: () => Promise<'granted' | 'denied'>;
  } | undefined)?.requestPermission;

  const grantAndAttach = async (): Promise<void> => {
    if (typeof REQUEST_PERMISSION === 'function') {
      try {
        const result = await REQUEST_PERMISSION();
        if (touchMode || scope.signal.aborted) return;
        if (result !== 'granted') {
          enableTitle.textContent = 'MOTION ACCESS DENIED';
          enableSub.textContent = 'Use the touch controls below to keep playing';
          return;
        }
      } catch {
        if (touchMode || scope.signal.aborted) return;
        enableTitle.textContent = 'MOTION ACCESS DENIED';
        enableSub.textContent = 'Use the touch controls below to keep playing';
        return;
      }
    }
    if (touchMode || scope.signal.aborted) return;
    motionEnabled = true;
    lastEventAt = performance.now();
    enableScrim.classList.add('hidden');
    scope.timeout(() => enableScrim.remove(), 400);
    // Small delay before attach — observed on some iOS PWAs the
    // first events go missing if the listener is registered inside
    // the same task as the permission resolution.
    scope.timeout(() => attach(), 0);
  };

  // The scrim is the ONLY tap target for the enable flow.
  enableScrim.addEventListener('click', () => { void grantAndAttach(); }, { signal: scope.signal });
  // On Android (no permission gate) we can attach straight away.
  if (typeof REQUEST_PERMISSION !== 'function') {
    motionEnabled = true;
    lastEventAt = performance.now();
    enableScrim.classList.add('hidden');
    scope.timeout(() => enableScrim.remove(), 400);
    attach();
  }
  try { if (sessionStorage.getItem('joystick:tilt-controls') === 'touch') useTouch(); } catch { /* Optional preference. */ }

  // Explicit re-centre button — once visible, this is the ONLY way to
  // rebaseline. Tapping the arena background does nothing, so stray
  // taps can't shift the neutral point.
  recentreBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    recentre();
  }, { signal: scope.signal });

  // ── Watchdog ───────────────────────────────────────────────────
  // A suspended sensor stream must stop movement on its first timeout.
  // Rebind once to recover iOS PWA streams, but keep the new baseline
  // unlocked so a changed hold cannot revive the old movement.
  scope.interval(() => {
    if (!active || document.hidden || sensorStale) return;
    const since = performance.now() - lastEventAt;
    if (since <= 1500) return;
    sensorStale = true;
    stopMotion();
    baselineLocked = false;
    liveDot.classList.remove('on');
    recentreBtn.textContent = 'TILT FROZEN · RE-CENTRE';
    touchButton.textContent = 'SENSOR UNAVAILABLE · USE TOUCH CONTROLS';
    detach();
    scope.timeout(() => attach(), 30);
  }, 250);

  // Stop before backgrounding can suspend timers. Motion permission
  // survives backgrounding, but listeners and the old hold do not.
  document.addEventListener('visibilitychange', () => {
    if (touchMode || !motionEnabled) return;
    if (document.hidden) {
      detach();
      stopMotion();
      baselineLocked = false;
      liveDot.classList.remove('on');
    } else {
      lastEventAt = performance.now();
      sensorStale = false;
      scope.timeout(() => attach(), 50);
    }
  }, { signal: scope.signal });
  scope.signal.addEventListener('abort', () => {
    detach();
    if (thrusting) stopMotion();
  }, { once: true });

  if(aCfg) queueMicrotask(()=> {
    if(scope.signal.aborted||!root.parentElement)return;
    const app=root.parentElement;
    const draw=()=> {
      const w=root.clientWidth,h=root.clientHeight;if(!w||!h)return;
      const bw=Math.min(180,w-24),bh=Math.min(80,h-24),x=w/2,y=h-bh/2-12;
      const map=editor!.resolve({A:{x,y,size:Math.min(bw,bh),shape:'rounded',zone:{x:x-bw/2,y:y-bh/2,width:bw,height:bh}}},w,h);
      const custom=editor!.editing()||!!readPositions(layoutKey(spec,app.clientWidth>app.clientHeight),['A']).A;
      if(custom) {const r=map.A!.zone;boost.style.cssText=`position:absolute;left:${r.x}px;top:${r.y}px;width:${r.width}px;height:${r.height}px;margin:0;z-index:20`;}
      else boost.removeAttribute('style');
    };
    editor=installLayoutEditor(app,root,spec,scope,draw);
    scope.addReset(()=>{stopMotion();baselineLocked=false;});
    const observer=new ResizeObserver(draw);observer.observe(root);draw();scope.signal.addEventListener('abort',()=>observer.disconnect(),{once:true});
  });

  return root;
}

/** Accelerometer-driven joystick — legacy half-pane variant. Phone is
 *  held flat; tilt direction + magnitude become the heading + power.
 *  Kept for compatibility with hosts that still mount tilt inside a
 *  split-pane spec (no current consumer; preserved so the existing
 *  smoke tests keep passing). */
function makeTilt(slot: string, send: SlotInput, scope: ControlScope): HTMLElement {
  const root = document.createElement('div');
  root.className = 'tilt';

  const level = document.createElement('div'); level.className = 'level';
  const bubble = document.createElement('div'); bubble.className = 'bubble';
  const ring = document.createElement('div'); ring.className = 'ring';
  level.appendChild(ring);
  level.appendChild(bubble);

  const hint = document.createElement('div'); hint.className = 'tilt-hint';
  hint.textContent = 'HOLD PHONE FLAT';

  const enable = document.createElement('button');
  enable.className = 'tilt-enable';
  enable.textContent = 'TAP TO ENABLE TILT';

  let active = false;
  let lastSendAt = 0;
  let lastAngle = NaN;
  let thrusting = false;
  // Tilt magnitude past which we consider the phone "in motion" (deadzone).
  const DEADZONE = 0.10;
  // Max tilt we map to power=1.0. ~35° of tilt feels like a hard pour.
  const MAX_TILT_DEG = 35;

  const onOrientation = (ev: DeviceOrientationEvent): void => {
    // beta = front-to-back tilt (-180..180). gamma = left-to-right (-90..90).
    const beta = ev.beta ?? 0;
    const gamma = ev.gamma ?? 0;
    // Normalise both to -1..+1 over MAX_TILT_DEG; clamp.
    const x = clamp(gamma / MAX_TILT_DEG, -1, 1);
    const y = clamp(beta  / MAX_TILT_DEG, -1, 1);
    const mag = Math.min(1, Math.hypot(x, y));
    const angle = Math.atan2(y, x);

    // Update the bubble overlay (visual feedback).
    const cx = 50 + x * 38;   // % of level box
    const cy = 50 + y * 38;
    bubble.style.left = `${cx}%`;
    bubble.style.top  = `${cy}%`;

    // Throttle to ~30Hz so the broker isn't drowning.
    const now = performance.now();
    if (now - lastSendAt < 33) return;
    lastSendAt = now;

    if (mag > DEADZONE) {
      if (!thrusting) { thrusting = true; send(`${slot}-thrust`, '1'); }
      if (!Number.isFinite(lastAngle) || Math.abs(angle - lastAngle) > 0.01) {
        lastAngle = angle;
        send(slot, String(Math.round(angle * 1000)));
      }
      send(`${slot}-power`, String(Math.round(mag * 1000)));
    } else if (thrusting) {
      thrusting = false;
      send(`${slot}-thrust`, '0');
      send(`${slot}-power`, '0');
    }
  };

  const attach = (): void => {
    if (active || scope.signal.aborted) return;
    active = true;
    enable.remove();
    hint.textContent = 'TILT TO STEER';
    window.addEventListener('deviceorientation', onOrientation, { capture: true, signal: scope.signal });
  };

  // iOS Safari (and any browser that gates motion access) exposes
  // requestPermission as a static method. If it exists, the listener
  // won't fire until the user grants — so gate behind the button. If
  // it doesn't exist, we can attach the listener right away.
  const REQUEST_PERMISSION = (DeviceOrientationEvent as unknown as {
    requestPermission?: () => Promise<'granted' | 'denied'>;
  }).requestPermission;
  if (typeof REQUEST_PERMISSION === 'function') {
    enable.addEventListener('click', async () => {
      try {
        const result = await REQUEST_PERMISSION();
        if (result === 'granted') attach();
        else { hint.textContent = 'MOTION ACCESS DENIED'; }
      } catch {
        hint.textContent = 'MOTION ACCESS DENIED';
      }
    });
  } else {
    // No permission API — attach immediately. Show the button anyway
    // for desktop testers (clicking it is harmless on devices with no
    // orientation sensor) and as a "ready" affordance.
    enable.addEventListener('click', attach);
    queueMicrotask(attach);
  }

  root.appendChild(level);
  root.appendChild(hint);
  root.appendChild(enable);
  return root;
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}
